// Umami analytics — privacy-respecting, production-only.
//
// Mirrors the env-gated pattern of ./rollbar.js: the plugin no-ops unless
// VITE_UMAMI_WEBSITE_ID is present, so local and the dev DigitalOcean build
// (which leave it unset) stay off — only the prod app sets the vars. Vite
// inlines VITE_* at build time, exactly like the Clerk/Rollbar keys.
//
// We deliberately do NOT use Umami's ad-blocker bypass (TRACKER_SCRIPT_NAME /
// COLLECT_API_ENDPOINT): if a blocker drops the script or the beacon, tracking
// simply doesn't happen — which is the point.

// User identification. Clerk's `user` can resolve before the deferred tracker
// script finishes loading, so we queue the identify call and flush it once Umami
// is ready.
let umamiReady = false;
let pendingIdentify = null; // { userId, accountType }

// ⚠️ ATTEMPT — unverified in prod. Umami can't filter/segment on custom session-data
// properties (only on a fixed field set that DOES include `tag`), so we mirror the
// account_type cohort onto every hit's `tag` via the data-before-send hook below to
// try to make it filterable. The documented way to set a tag is the tracker's
// `data-tag` attribute; setting payload.tag from before-send is undocumented and may
// not populate the column. Latched as soon as the cohort is known (see identifyUmami).
let currentTag = null;

function sendIdentify(userId, accountType) {
  // No name/email by design: Umami holds just an opaque, pseudonymous key matching
  // the Rollbar person id, so a session can be cross-referenced with an error
  // without storing PII. The { id } property makes the id filterable; `account_type`
  // is a PII-free cohort (internal / client / self_service) computed in App.vue from
  // the email domain + managed-billing flag — the email itself never reaches here.
  if (window.umami) {
    window.umami.identify(userId, accountType ? { id: userId, account_type: accountType } : { id: userId });
  }
}

// Called from App.vue with the Clerk user id (the same value set on Rollbar) and,
// once known, the PII-free account_type cohort. Safe no-op when Umami is disabled
// (env absent / DNT / GPC): the load listener below is never registered, so the
// call just stays queued and is never sent.
export function identifyUmami(userId, accountType) {
  if (!userId) return;
  // Latch the cohort as the Umami tag as soon as it's known (see currentTag above).
  // Only when truthy, so an id-only call (cohort not resolved yet) never clears it.
  if (accountType) currentTag = accountType;
  if (umamiReady) {
    sendIdentify(userId, accountType);
  } else {
    pendingIdentify = { userId, accountType };
  }
}

// Collapse Mongo ObjectId path segments (workspace/bot/… ids) so per-id routes
// group as ONE page in Umami's report — e.g. /app/workspace/<id>/bots/<id>/storage
// → /app/workspace/:id/bots/:id/storage. Applied to pageview + event URLs via the
// data-before-send hook in install(). Only matches exactly-24-hex segments between
// slashes, so it won't touch query strings or non-id paths.
const collapseIds = (value) =>
  typeof value === 'string'
    ? value.replace(/\/[0-9a-f]{24}(?=\/|$|\?|#)/gi, '/:id')
    : value;

export default {
  install() {
    const websiteId = import.meta.env.VITE_UMAMI_WEBSITE_ID;
    if (!websiteId) {
      Console.log('Umami disabled (no website id).');
      return;
    }

    // Respect the user's opt-out BEFORE injecting anything, so opted-out users
    // trigger zero network requests (not even the tracker download): legacy
    // Do Not Track (Firefox/IE variants) and the modern Global Privacy Control.
    const optedOut =
      navigator.doNotTrack === '1' ||
      navigator.doNotTrack === 'yes' ||
      window.doNotTrack === '1' ||
      navigator.msDoNotTrack === '1' ||
      navigator.globalPrivacyControl === true;
    if (optedOut) {
      Console.log('Umami disabled (user opted out via DNT/GPC).');
      return;
    }

    // Normalise URLs before each send so dynamic routes group in the Pages report.
    // Umami resolves data-before-send as window[<name>], so define it before load.
    window.__umamiBeforeSend = (type, payload) => {
      if (payload) {
        if (payload.url) payload.url = collapseIds(payload.url);
        if (payload.referrer) payload.referrer = collapseIds(payload.referrer);
        // ATTEMPT: tag every hit with the account_type cohort so it becomes
        // filterable. Hits fired before the cohort resolves stay untagged.
        if (currentTag) payload.tag = currentTag;
      }
      return payload; // a falsy return would cancel the send
    };

    const script = document.createElement('script');
    script.defer = true;
    script.src = import.meta.env.VITE_UMAMI_SRC;
    script.setAttribute('data-website-id', websiteId);
    // Belt-and-suspenders: even if the gate above ever misses an edge case,
    // Umami self-suppresses when the browser signals Do Not Track.
    script.setAttribute('data-do-not-track', 'true');
    script.setAttribute('data-before-send', '__umamiBeforeSend');
    const domains = import.meta.env.VITE_UMAMI_DOMAINS;
    if (domains) {
      script.setAttribute('data-domains', domains);
    }
    // Flush a queued identify once the tracker is ready (see identifyUmami above).
    script.addEventListener('load', () => {
      umamiReady = true;
      if (pendingIdentify) {
        sendIdentify(pendingIdentify.userId, pendingIdentify.accountType);
        pendingIdentify = null;
      }
    });
    document.head.appendChild(script);

    // Reuse the app's existing instrumentation: forward clicks on tagged elements
    // to Umami as custom events. Singleton actions carry id="btn-…"; repeated/list
    // items carry data-telemetry-id. Inputs and layout containers are intentionally
    // excluded (they don't match the selector). Capture phase so we still catch
    // clicks on elements whose handlers call stopPropagation.
    document.addEventListener(
      'click',
      (e) => {
        const el = e.target.closest?.('[data-telemetry-id], [id^="btn-"]');
        if (el && window.umami) {
          window.umami.track(el.dataset.telemetryId || el.id);
        }
      },
      true,
    );

    Console.log('Umami running.');
  },
};
